import React from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';

import { blockHeading } from '../panelHeading';

export interface IProfileDetailField {
  label: string;
  /** Pre-formatted by the caller — a date, a masked identifier, a consent word. */
  value: string;
  /**
   * A field the viewer is not permitted to read. The frame draws `🔒 Restricted` in the value slot
   * (`GEO COORDINATES`), so the marker is text and the field keeps its label.
   */
  restricted?: boolean;
}

export interface IProfileDetailGroup {
  key: string;
  /** `Identity`, `Address`, `Contact`, `Custom Fields`, `Permissions & Availability`. */
  title: string;
  icon?: React.ReactNode;
  fields: IProfileDetailField[];
  /**
   * `3583-10401` and its three siblings run their fields two-up inside a 297px card; the taller
   * consent card beside them runs one per row.
   */
  columns?: number;
  /** Slot for the group's own trailing affordance — the `View More` link the frame ends on. */
  footer?: React.ReactNode;
}

export interface IProfileDetailListProps {
  groups: IProfileDetailGroup[];
  /** Names the whole set for assistive technology; the group titles name each panel. */
  label?: string;
  className?: string;
}

const DEFAULT_COLUMNS = 2;

/* The identity cards are 297px wide and set 42px apart (3583-10401 at x=331 against 3583-10438 at
   x=670); 40 is the nearest step on the space scale. */
const GROUP_MIN_WIDTH = '240px';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${GROUP_MIN_WIDTH}, 1fr));
  gap: ${tokens.space['40']};
`;

/* OUTLINE, no fill. 3583-10438 (Rectangle 734, the Address tile's own surface layer) carries radius
   12 and NO fill, and the 100% capture A-z-3583-10457.png shows the interiors of Address and Contact
   reading the same value as the section surface behind them, separated from it only by a hairline.
   The surface-card fill this used to paint also double-filled every Product Readiness use, where the
   Panel around it already paints surface-card. PLA-8828. */
const Group = styled.section`
  display: flex;
  flex-direction: column;

  /* A grid item's automatic minimum is its min-content width, so a group whose longest field is
     wider than its track pushed past the list instead of shrinking with it. PLA-8844. */
  min-width: 0;
  padding: ${tokens.space['16']};
  border: 1px solid ${tokens.color['border-subtle']};
  border-radius: ${tokens.radius.md};
`;

const GroupHeader = styled.header`
  display: flex;
  align-items: center;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: ${tokens.space['8']};
  }
`;

const GroupIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;

  /* The badge the frame draws behind each group glyph, measured off the 100% canvas: 26 square,
     radius 6, ink #13284D. The radius has no token - the scale has no 6 step (xs 4, sm 8) - so it
     stays a literal; the ink is --is-chip-surface since the PERX3-REDESIGN token pass. PLA-8828. */
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: ${tokens.color['chip-surface']};
  font-size: 14px;
  line-height: 1;
`;

const GroupTitle = styled.h4`
  margin: 0;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['detail-group-title-size']};
  font-weight: ${tokens.type['detail-group-title-weight']};
  line-height: ${tokens.type['detail-group-title-line']};
`;

const FieldGrid = styled.dl<{ $columns: number }>`
  display: grid;
  margin: ${tokens.space['16']} 0 0;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: ${tokens.space['12']} ${tokens.space['16']};
`;

const Field = styled.div`
  min-width: 0;
`;

const FieldLabel = styled.dt`
  ${blockHeading}

  /* 3583-10313: Inter 400 / 10 / 12 with 0.6px tracking, against the block label's 800 / 0.8. The
     ink is its own role, --is-field-label: neither --is-text-muted #8592AD nor --is-filter-text-muted
     #8DA0BD matches it, so the PERX3-REDESIGN pass gave it a key rather than folding it in. */
  && {
    color: ${tokens.color['field-label']};
    font-weight: ${tokens.type['body-weight']};
    letter-spacing: 0.6px;
  }
`;

const FieldValue = styled.dd<{ $restricted: boolean }>`
  margin: ${tokens.space['4']} 0 0;
  color: ${({ $restricted }) =>
    $restricted ? tokens.color['text-muted'] : tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
  overflow-wrap: anywhere;
`;

const GroupFooter = styled.div`
  display: flex;
  justify-content: flex-end;

  && {
    margin-top: auto;
    padding-top: ${tokens.space['12']};
  }
`;

const ProfileDetailList: React.FC<IProfileDetailListProps> = ({ groups, label, className }) => {
  const rendered = (groups ?? []).filter(group => group.fields?.length > 0 || group.footer);

  // The FR forbids fabricating a profile, so nothing readable leaves no titled shells behind - the
  // card shows its own data state instead.
  if (rendered.length === 0) {
    return null;
  }

  return (
    <Grid className={className} aria-label={label} data-testid="profile-detail-list">
      {rendered.map(group => (
        <Group key={group.key} aria-label={group.title} data-testid="profile-detail-group">
          <GroupHeader>
            {group.icon ? <GroupIcon aria-hidden="true">{group.icon}</GroupIcon> : null}
            <GroupTitle data-testid="profile-detail-group-title">{group.title}</GroupTitle>
          </GroupHeader>

          {group.fields?.length > 0 ? (
            <FieldGrid
              $columns={group.columns || DEFAULT_COLUMNS}
              data-columns={group.columns || DEFAULT_COLUMNS}
            >
              {/* Keyed by position, not by label: a group's fields are a positional list and the
                  designs repeat a label within one — Customer Profile draws `Lorem Ipsum` three
                  times in Identity — which collided as a duplicate React key. PLA-8828. */}
              {group.fields.map((field, index) => (
                <Field key={`${group.key}-${index}`} data-testid="profile-detail-field">
                  <FieldLabel data-testid="profile-detail-field-label">{field.label}</FieldLabel>
                  <FieldValue
                    $restricted={Boolean(field.restricted)}
                    data-restricted={field.restricted ? 'true' : 'false'}
                    data-testid="profile-detail-field-value"
                  >
                    {field.value}
                  </FieldValue>
                </Field>
              ))}
            </FieldGrid>
          ) : null}

          {group.footer ? <GroupFooter>{group.footer}</GroupFooter> : null}
        </Group>
      ))}
    </Grid>
  );
};

export default ProfileDetailList;
